import axios from "axios"
import { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { FALLBACK_PRODUCTS, SITE_NAME, getBaseUrl, getPageMeta } from "./seoConfig"

const DESCRIPTION_SELECTOR = 'meta[name="description"]'
const CANONICAL_SELECTOR = 'link[rel="canonical"]'
const OG_TITLE_SELECTOR = 'meta[property="og:title"]'
const OG_DESCRIPTION_SELECTOR = 'meta[property="og:description"]'
const OG_URL_SELECTOR = 'meta[property="og:url"]'
const OG_TYPE_SELECTOR = 'meta[property="og:type"]'

function isPublicProduct(product) {
    return !product?.hidden
}

function mergeProducts(primaryProducts, fallbackProducts = FALLBACK_PRODUCTS) {
    const productMap = new Map()

    fallbackProducts.forEach((product) => {
        productMap.set(String(product.key), product)
    })

    primaryProducts.forEach((product) => {
        productMap.set(String(product.key), product)
    })

    return Array.from(productMap.values()).filter(isPublicProduct)
}

function ensureMeta(selector, createElement) {
    const existingElement = document.head.querySelector(selector)

    if (existingElement) return existingElement

    const element = createElement()
    document.head.appendChild(element)
    return element
}

function setNamedMeta(selector, name, content) {
    const element = ensureMeta(selector, () => {
        const meta = document.createElement("meta")
        meta.setAttribute("name", name)
        return meta
    })

    element.setAttribute("content", content)
}

function setPropertyMeta(selector, property, content) {
    const element = ensureMeta(selector, () => {
        const meta = document.createElement("meta")
        meta.setAttribute("property", property)
        return meta
    })

    element.setAttribute("content", content)
}

export default function SeoMeta() {
    const location = useLocation()
    const [products, setProducts] = useState([])

    useEffect(() => {
        if (!location.pathname.startsWith("/product")) return undefined

        let isActive = true

        async function fetchProducts() {
            try {
                const response = await axios.get("/api/products")

                if (isActive) {
                    setProducts((response.data.data ?? []).filter(isPublicProduct))
                }
            } catch (error) {
                if (isActive) {
                    setProducts([])
                }
            }
        }

        fetchProducts()

        return () => {
            isActive = false
        }
    }, [location.pathname])

    const meta = useMemo(() => {
        const baseUrl = getBaseUrl()
        const productId = location.pathname.match(/^\/product\/([^/]+)$/)?.[1]
        const availableProducts = mergeProducts(products)
        const currentProduct = productId
            ? availableProducts.find((item) => String(item.key) === decodeURIComponent(productId))
            : null
        const pageMeta = getPageMeta(location.pathname, currentProduct)

        return {
            ...pageMeta,
            url: new URL(location.pathname, baseUrl).toString(),
        }
    }, [location.pathname, products])

    useEffect(() => {
        document.title = meta.title

        setNamedMeta(DESCRIPTION_SELECTOR, "description", meta.description)
        setPropertyMeta(OG_TITLE_SELECTOR, "og:title", meta.title)
        setPropertyMeta(OG_DESCRIPTION_SELECTOR, "og:description", meta.description)
        setPropertyMeta(OG_URL_SELECTOR, "og:url", meta.url)
        setPropertyMeta(OG_TYPE_SELECTOR, "og:type", "website")

        const canonical = ensureMeta(CANONICAL_SELECTOR, () => {
            const link = document.createElement("link")
            link.setAttribute("rel", "canonical")
            return link
        })

        canonical.setAttribute("href", meta.url)
    }, [meta])

    useEffect(() => {
        document.documentElement.setAttribute("lang", "ru")
        document.documentElement.setAttribute("prefix", `og: https://ogp.me/ns#`)
        setPropertyMeta('meta[property="og:site_name"]', "og:site_name", SITE_NAME)
    }, [])

    return null
}
